"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { Theme } from "@astryxdesign/core/theme";
import type { ThemeMode } from "@astryxdesign/core/theme";
import { neutralTheme } from "@astryxdesign/theme-neutral/built";
import {
  readStoredColorMode,
  storeColorMode,
  type ColorMode,
} from "@/lib/theme/color-mode";

type ColorModeContextValue = {
  mode: ColorMode;
  setMode: (mode: ColorMode) => void;
};

const ColorModeContext = createContext<ColorModeContextValue | null>(null);

export function ColorModeProvider({
  children,
  initialMode = "system",
}: {
  children: React.ReactNode;
  initialMode?: ColorMode;
}) {
  // На клиенте сразу читаем localStorage (как bootstrap-скрипт), без повторного useEffect —
  // иначе Astryx Theme на гидрации снимает data-theme и панели мигают.
  const [mode, setModeState] = useState<ColorMode>(() => {
    if (typeof window !== "undefined") {
      return readStoredColorMode();
    }
    return initialMode;
  });

  const setMode = useCallback((next: ColorMode) => {
    setModeState(next);
    storeColorMode(next);
  }, []);

  const value = useMemo(() => ({ mode, setMode }), [mode, setMode]);

  return (
    <ColorModeContext.Provider value={value}>
      <Theme theme={neutralTheme} mode={mode as ThemeMode}>
        {children}
      </Theme>
    </ColorModeContext.Provider>
  );
}

export function useColorMode(): ColorModeContextValue {
  const ctx = useContext(ColorModeContext);
  if (!ctx) {
    throw new Error("useColorMode must be used within ColorModeProvider");
  }
  return ctx;
}
